import { useEffect, useMemo, useState } from "react"
import Script from "next/script"

import { env } from "@/lib/env"
import { getBrowserLanguageRegion } from "@/lib/locale"
import { readLocaleCookie, setLocaleCookie } from "@/lib/locale-cookie"
import {
  setMindtripLocaleHeaderEnabled,
  setResolvedLocale,
} from "@/lib/locale-headers"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import {
  selectEmbeddedLocale,
  selectLanguage,
  selectLocale,
} from "@/lib/storage/locale-selectors"
import { setBrowserLocale } from "@/lib/storage/locale-slice"
import { setWeglotLanguage } from "@/lib/weglot"

// Always mounts so formatters localize even with Weglot off (a non-Mindtrip web user
// still needs localized dates): the device-signal effect runs unconditionally; the
// Weglot <Script> and the ready/sink effects gate on `weglotEnabled`.
export function LocaleHandler({ translate = true }) {
  const dispatch = useAppDispatch()
  const resolvedLocale = useAppSelector(selectLocale)
  const language = useAppSelector(selectLanguage)
  const embedded = !!useAppSelector(selectEmbeddedLocale)
  const apiKey = env("NEXT_PUBLIC_WEGLOT_API_KEY")

  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [weglotReady, setWeglotReady] = useState(false)

  const enabled = translate
  // only load weglot when non-English language detected
  const weglotEnabled = enabled && (scriptLoaded || language !== "en")

  // Device signals → store. Client-only post-commit so server and first client render
  // both see browser={}, avoiding a hydration mismatch.
  useEffect(() => {
    const { browserLanguage, browserRegion } = getBrowserLanguageRegion()
    dispatch(
      setBrowserLocale({ language: browserLanguage, region: browserRegion })
    )
  }, [dispatch])

  // Keep the cookie's value in step with the resolved locale so getServerSideProps and
  // server-axios (X-Mindtrip-Locale) read the same locale the client resolved. Embedded
  // never writes it (setLocaleCookie no-ops there — the iframe follows the host instead).
  useEffect(() => {
    if (resolvedLocale && readLocaleCookie() !== resolvedLocale) {
      setLocaleCookie(resolvedLocale)
    }
  }, [resolvedLocale])

  // Sync resolved locale + the client-side header gate for the non-React consumers
  useMemo(() => {
    setResolvedLocale(resolvedLocale)
  }, [resolvedLocale])

  useMemo(() => {
    setMindtripLocaleHeaderEnabled(enabled)
  }, [enabled])

  // Gate readiness on Weglot's INITIAL translation actually landing — not just the
  // `initialized` event. Weglot boots into its stored/detected language and applies
  // that translation asynchronously, *after* `initialized` fires. Switching while that
  // pass is in flight starts a second pass that races it, and the wrong one can win.
  // So once initialized: if Weglot booted the source language ("en") there's no pass
  // and we're ready now; otherwise wait for the pass's `languageChanged` first. This
  // only delays the language↔language load case — the edge case where it's acceptable.
  useEffect(() => {
    if (!weglotEnabled || !scriptLoaded) return
    const markReady = () => setWeglotReady(true)
    const onInitialized = () => {
      if (window.Weglot?.getCurrentLang() === "en") {
        markReady()
      } else {
        window.Weglot?.on("languageChanged", markReady)
      }
    }
    if (window.Weglot?.initialized) {
      onInitialized()
    } else {
      window.Weglot?.on("initialized", onInitialized)
    }
    return () => {
      window.Weglot?.off("initialized", onInitialized)
      window.Weglot?.off("languageChanged", markReady)
    }
  }, [weglotEnabled, scriptLoaded])

  // Translate the UI into the resolved locale (which already folds in saved, embedded
  // and browser sources). switchTo no-ops on a match and on the "en" default, so this
  // settles without looping.
  useEffect(() => {
    if (!weglotEnabled || !weglotReady || !resolvedLocale) return
    setWeglotLanguage(resolvedLocale, embedded)
  }, [weglotEnabled, weglotReady, resolvedLocale, embedded])

  if (!weglotEnabled || !apiKey) return null

  return (
    <Script
      src="https://cdn.weglot.com/weglot-5.88.0.min.js"
      onReady={() => {
        window.Weglot?.initialize({
          api_key: apiKey,
          hide_switcher: true,
          // next.js mirrors document.title into this aria-live node on client navigation
          excluded_blocks: [{ value: "next-route-announcer" }],
        })
        setScriptLoaded(true)
      }}
    />
  )
}
